import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMegaResult } from '../../../services/assessmentService';

const labels = ['Q&L', 'Cog', 'Skill', 'Verbal', 'Data', 'Code', 'SJT'];

function buildRadarPoints(scores) {
  const center = 150;
  const radius = 110;
  return scores.map((score, idx) => {
    const angle = (Math.PI * 2 * idx) / scores.length - Math.PI / 2;
    const r = (Math.max(0, Math.min(100, score)) / 100) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  });
}

function polygonString(points) {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}

export default function ResultPage() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);

  useEffect(() => {
    const preload = async () => {
      const cached = sessionStorage.getItem('mega_result_payload');
      if (cached) {
        setResult(JSON.parse(cached));
        return;
      }

      const sessionId = sessionStorage.getItem('mega_session_id');
      if (sessionId) {
        const payload = await getMegaResult(sessionId);
        setResult(payload);
      }
    };

    preload();
  }, []);

  const sectionRows = useMemo(() => result?.sectionScores || [], [result]);

  const scoreMap = useMemo(() => {
    const map = {
      MEG_QL: 0,
      MEG_CB: 0,
      MEG_ST: 0,
      MEG_VC: 0,
      MEG_DA: 0,
      MEG_CF: 0,
      MEG_SJ: 0
    };

    sectionRows.forEach((row) => {
      map[row.sectionCode] = row.score;
    });

    return map;
  }, [sectionRows]);

  const radarScores = [
    scoreMap.MEG_QL,
    scoreMap.MEG_CB,
    scoreMap.MEG_ST,
    scoreMap.MEG_VC,
    scoreMap.MEG_DA,
    scoreMap.MEG_CF,
    scoreMap.MEG_SJ
  ];

  const radarPoints = buildRadarPoints(radarScores);
  const apexScore = Math.round(Number(result?.scaledScore || 0));
  const percentile = Math.max(1, Math.min(99, Math.round(Number(result?.percentile || 0) || (apexScore / 10))));

  if (!result) {
    return <div className="min-h-screen mega-intro-bg flex items-center justify-center text-white">Loading results...</div>;
  }

  return (
    <div className="min-h-screen mega-intro-bg px-4 py-10 text-white">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-cabinet text-center">Apex All-in-One Mega Bundle</h1>
        <p className="text-center text-white/70 mt-2">Premium Assessment Dashboard</p>

        <div className="mt-8 mega-panel rounded-3xl p-6 md:p-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-white/70">Apex Score</p>
              <p className="text-6xl font-bold mt-2 text-yellow-100">{apexScore}</p>
              <p className="text-sm text-white/75 mt-1">Scale: 0 to 1000</p>
              <p className="mt-4 rounded-full inline-block border border-yellow-200/40 bg-yellow-200/15 px-3 py-1 text-sm text-yellow-100">
                Top {percentile}% of all test takers
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full bg-sky-400/20 border border-sky-300/30 text-sky-100 text-xs">Coding Score: {Math.round(scoreMap.MEG_CF)}%</span>
                <span className="px-3 py-1 rounded-full bg-amber-400/20 border border-amber-300/30 text-amber-100 text-xs">SJT Score: {Math.round(scoreMap.MEG_SJ)}%</span>
              </div>
            </div>

            <div className="flex justify-center">
              <svg viewBox="0 0 300 300" className="w-[320px] h-[320px]">
                {[1, 2, 3, 4].map((layer) => {
                  const layerPoints = buildRadarPoints(Array(7).fill((layer / 4) * 100));
                  return (
                    <polygon
                      key={`grid_${layer}`}
                      points={polygonString(layerPoints)}
                      fill="none"
                      stroke="rgba(255,255,255,0.18)"
                      strokeWidth="1"
                    />
                  );
                })}
                <polygon points={polygonString(radarPoints)} fill="rgba(245,158,11,0.35)" stroke="#fbbf24" strokeWidth="2" />
                {labels.map((label, idx) => {
                  const angle = (Math.PI * 2 * idx) / labels.length - Math.PI / 2;
                  const x = 150 + 125 * Math.cos(angle);
                  const y = 150 + 125 * Math.sin(angle);
                  return (
                    <text key={label} x={x} y={y} textAnchor="middle" className="fill-white text-[11px]">
                      {label}
                    </text>
                  );
                })}
              </svg>
            </div>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/20 text-white/70">
                  <th className="py-2">Section</th>
                  <th className="py-2">Score</th>
                  <th className="py-2">Time Taken</th>
                  <th className="py-2">Accuracy</th>
                </tr>
              </thead>
              <tbody>
                {sectionRows.map((row) => (
                  <tr key={row.sectionCode} className="border-b border-white/10">
                    <td className="py-2">{row.sectionName}</td>
                    <td className="py-2">{row.score}%</td>
                    <td className="py-2">{row.timeTaken}</td>
                    <td className="py-2">{row.accuracy}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 rounded-xl border border-white/20 bg-white/10 p-4 overflow-x-auto">
            <p className="text-sm text-white/75 mb-2">Industry Readiness Bands</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/20 text-white/70">
                  <th className="py-1.5">Band</th>
                  <th className="py-1.5">Current Status</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/10"><td className="py-1.5">Executive Ready</td><td className="py-1.5">{apexScore >= 850 ? 'Qualified' : 'Not Yet'}</td></tr>
                <tr className="border-b border-white/10"><td className="py-1.5">Senior Ready</td><td className="py-1.5">{apexScore >= 700 ? 'Qualified' : 'Not Yet'}</td></tr>
                <tr className="border-b border-white/10"><td className="py-1.5">Associate Ready</td><td className="py-1.5">{apexScore >= 550 ? 'Qualified' : 'Not Yet'}</td></tr>
                <tr><td className="py-1.5">Developing</td><td className="py-1.5">{apexScore < 550 ? 'Current Band' : 'Above Band'}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-xl bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-600 px-4 py-2.5 text-slate-900 font-semibold"
            >
              Download Premium Report (PDF)
            </button>
            <button
              type="button"
              onClick={() => navigate('/assessment')}
              className="rounded-xl border border-white/25 bg-white/10 px-4 py-2.5"
            >
              Back to Hub
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
